import React, { Component, createRef } from 'react'
import { reduxForm, Field } from 'redux-form'
import classnames from 'classnames'

import { InputField, SelectField } from '../../components/inputs'
import LoaderButton from '../../components/loaderButton'
import { PHONE_MASK, PHONE_PLACEHOLDER } from '../../utils/phoneMask'
import { ReduxFormProps } from '../../forms'
import AdminProfilePresets from './adminProfilePresets'
import BadgesList from './badgesList'
import { badges as allBadges } from './badges'

export type Callback<T = void> = (...args: any) => T

export interface Props {
  cancel: Callback
  title: string
  organizations?: Array<{ id: string; nameLegal?: string; code?: string }>
  // Only Create passes this on -- Edit keeps badge management on the
  // dedicated Admin Profile screen (/admins/:id), unchanged.
  showBadges?: boolean
  // Only superusers may view/assign the SUPERUSER badge.
  canAssignSuperuser?: boolean
  // An existing admin keeps the organization they were created in
  // (blinker-legacy#86); the backend refuses the change either way.
  organizationLocked?: boolean
  // Create Admin only. Editing an admin who has no organization still saves
  // without one; production has none, but the edit screen must not block it.
  requireOrganization?: boolean
}

export interface FormProps {
  email: string
  firstName: string
  lastName: string
  phone?: string
  calltoolsAgentId?: string
  organizationId?: string
  password: string
  passwordConfirmation: string
  badges?: string[]
}

interface AdminFormState {
  selectedBadges: string[]
  badgesExpanded: boolean
  badgesContentHeight: number
}

export class AdminForm extends Component<
  ReduxFormProps<Props, FormProps>,
  AdminFormState
> {
  static displayName = 'AdminForm'
  state: AdminFormState = {
    selectedBadges: [],
    badgesExpanded: false,
    badgesContentHeight: 0,
  }

  componentDidMount() {
    const { initialValues } = this.props
    const badges = initialValues?.badges
    if (badges && badges.length > 0) {
      this.setState({ selectedBadges: [...badges] })
    }
  }

  componentDidUpdate(
    prevProps: ReduxFormProps<Props, FormProps>,
    prevState: AdminFormState
  ) {
    // Surface the collapsed checklist the moment a no-badges submit is
    // rejected, so the error message isn't hidden behind a closed section.
    if (
      this.props.submitFailed &&
      !prevProps.submitFailed &&
      this.state.selectedBadges.length === 0
    ) {
      this.setState({ badgesExpanded: true })
      return
    }

    if (!prevState.badgesExpanded && this.state.badgesExpanded) {
      const height = this.badgesContent.current?.scrollHeight || 0
      if (height !== this.state.badgesContentHeight) {
        this.setState({ badgesContentHeight: height })
      }
    }
  }

  // Mirrors components/collapse.tsx's own open/close height measurement,
  // so this accordion matches the one other collapsible sections already use.
  private badgesContent = createRef<HTMLDivElement>()

  onChangePreset = (values) => {
    // Clicking a preset is the moment the creator most wants to see what
    // they just picked, so auto-expand the full checklist for review.
    //
    // The redux-form `badges` value is what the checkboxes render from and
    // what validate()/onSubmit read, so write it here. BadgesList only syncs
    // component state into the form when the *count* differs, which leaves a
    // same-size swap (e.g. RB Agent -> RB Manager) stale.
    this.props.change('badges', [...values])
    this.setState({ selectedBadges: values, badgesExpanded: true })
  }

  toggleBadgesExpanded = () => {
    this.setState((state) => ({ badgesExpanded: !state.badgesExpanded }))
  }

  onChangeBadges = (event) => {
    const value = event.target.value
    const selectedValues = [...this.state.selectedBadges]
    if (selectedValues.includes(value)) {
      selectedValues.splice(selectedValues.indexOf(value), 1)
    } else {
      selectedValues.push(value)
    }
    this.setState({ selectedBadges: selectedValues })
  }

  render() {
    const {
      title,
      handleSubmit,
      submitting,
      cancel,
      organizations = [],
      showBadges = false,
      canAssignSuperuser = false,
      organizationLocked = false,
    } = this.props

    const badgeOptions = (
      canAssignSuperuser
        ? allBadges
        : allBadges.filter((badge) => badge !== 'SUPERUSER')
    ).map((badge) => ({ label: badge, value: badge }))

    return (
      <form className="form" name="adminForm" onSubmit={handleSubmit}>
        <div
          className="admin-form"
          // disabled={submitting}
        >
          <div className="admin-form__header">
            <h1 className="form__header">{title}</h1>
            <hr className="form__hr admin-form__header-hr" />
          </div>
          <Field
            type="text"
            label="First Name"
            name="firstName"
            component={InputField}
            data-testid="adminFormFirstNameInput"
          />
          <Field
            type="text"
            label="Last Name"
            name="lastName"
            component={InputField}
            data-testid="adminFormLastNameInput"
          />
          <Field
            type="text"
            label="Email"
            name="email"
            component={InputField}
            data-testid="adminFormEmailInput"
          />
          <Field
            type="text"
            label="Phone"
            name="phone"
            component={InputField}
            placeholder={PHONE_PLACEHOLDER}
            mask={PHONE_MASK}
            startAdornment="+1"
            data-testid="adminFormPhoneInput"
          />
          <Field
            type="text"
            label="Calltools Agent ID"
            name="calltoolsAgentId"
            component={InputField}
            data-testid="adminFormCalltoolsAgentIdInput"
          />
          {/* One grid cell: the form is a two-column grid, so a separate
              element for the hint landed in the next free cell (under
              Calltools Agent ID) instead of under the dropdown. */}
          <div className="admin-form__organization">
            <Field
              label="Organization"
              name="organizationId"
              component={SelectField}
              data-testid="adminFormOrganizationSelect"
              disabled={organizationLocked}
            >
              <option value="">— None —</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nameLegal || o.code || 'Org'} (#{o.id})
                </option>
              ))}
            </Field>
            {organizationLocked && (
              <p
                className="form__help-text admin-form__organization-hint"
                data-testid="adminFormOrganizationLockedHint"
              >
                To work in another organization, create a new user there.
              </p>
            )}
          </div>
          <Field
            type="text"
            label="Password"
            name="password"
            component={InputField}
            data-testid="adminFormPasswordInput"
          />
          <Field
            type="text"
            label="Retype password (to confirm)"
            name="passwordConfirmation"
            component={InputField}
            data-testid="adminFormPasswordConfirmationInput"
          />
          {showBadges && (
            <div className="admin-form__permissions badges-form">
              <h2 className="badges-form__header">Permissions</h2>
              <AdminProfilePresets
                onChange={this.onChangePreset}
                selectedBadges={this.state.selectedBadges}
              />
              <div className="collapse badges-form__collapse">
                <button
                  type="button"
                  className={classnames('collapse__button', {
                    'collapse__button--open': this.state.badgesExpanded,
                  })}
                >
                  Toggle
                </button>
                <div
                  className="collapse__header"
                  onClick={this.toggleBadgesExpanded}
                  data-testid="badges-form-toggle"
                >
                  Full badge list ({this.state.selectedBadges.length} selected)
                </div>
                <div
                  className={classnames('collapse__wrapper', {
                    'collapse__wrapper--open': this.state.badgesExpanded,
                  })}
                  style={{
                    height: this.state.badgesExpanded
                      ? this.state.badgesContentHeight
                      : 0,
                  }}
                >
                  <div className="collapse__content" ref={this.badgesContent}>
                    <BadgesList
                      name="badges"
                      options={badgeOptions}
                      selectedValues={this.state.selectedBadges}
                      onChangeCallback={this.onChangeBadges}
                    />
                  </div>
                </div>
              </div>
              {this.props.submitFailed &&
                this.state.selectedBadges.length === 0 && (
                  <p className="form__help-text text-error">
                    Select a preset or at least one badge before saving.
                  </p>
                )}
            </div>
          )}
          <div className="admin-form__buttons">
            <LoaderButton
              data-testid="admin-form-save-button"
              type="submit"
              className="button button--primary-round admin-form__primary-button"
              isLoading={submitting}
            >
              Save
            </LoaderButton>
            <a
              onClick={cancel}
              data-testid="adminFormCancelButton"
              className="button button--secondary-round"
              aria-label="cancel"
            >
              Cancel
            </a>
          </div>
        </div>
      </form>
    )
  }
}

// Mirrors the backend password complexity rule:
// config.password_regex = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/  (devise.rb)
// config.password_length = 10..128
const PASSWORD_COMPLEXITY_REGEX = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/
const PASSWORD_MIN_LENGTH = 10

// The Agent ID field itself is free-form; this only gates what the dialer error
// page may prefill/display (CallTools ids are UUIDs).
export const UUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

// Mirrors User#calltools_agent_id on the backend: any string is an id, but not
// one with whitespace, control or zero-width characters inside it. Leading and
// trailing ones (copy-paste) are trimmed before saving, never rejected.
export const CALLTOOLS_AGENT_ID_MAX_LENGTH = 255
const AGENT_ID_INVISIBLE =
  '\\s\\u0000-\\u001F\\u007F-\\u009F\\u00AD\\u200B-\\u200F\\u202A-\\u202E\\u2060-\\u2064\\uFEFF'
const AGENT_ID_EDGES = new RegExp(
  `^[${AGENT_ID_INVISIBLE}]+|[${AGENT_ID_INVISIBLE}]+$`,
  'g'
)
const AGENT_ID_INVALID_CHARS = new RegExp(`[${AGENT_ID_INVISIBLE}]`)

export const trimAgentId = (value?: string | null) =>
  (value ?? '').replace(AGENT_ID_EDGES, '')

export const agentIdError = (value?: string | null) => {
  const id = trimAgentId(value)
  if (AGENT_ID_INVALID_CHARS.test(id)) {
    return 'Must not contain spaces or control characters'
  }
  if (id.length > CALLTOOLS_AGENT_ID_MAX_LENGTH) {
    return `Must be ${CALLTOOLS_AGENT_ID_MAX_LENGTH} characters or fewer`
  }
  return undefined
}

export const validate = (data: FormProps, props: Props) => {
  const {
    email,
    firstName,
    lastName,
    password,
    calltoolsAgentId,
    badges,
    organizationId,
  } = data
  const errors: Partial<Record<keyof FormProps, string>> = {}

  if (!email) {
    errors.email = 'Email required'
  }

  if (!firstName) {
    errors.firstName = 'First name required'
  }

  if (!lastName) {
    errors.lastName = 'Last name required'
  }

  // An admin without an organization is a customer to the backend: it reaches
  // no customers or deals and cannot be given permissions (blinker-legacy#86).
  if (props?.requireOrganization && !organizationId) {
    errors.organizationId = 'Organization required'
  }

  const agentIdProblem = agentIdError(calltoolsAgentId)
  if (agentIdProblem) {
    errors.calltoolsAgentId = agentIdProblem
  }

  if (password) {
    if (password.length < PASSWORD_MIN_LENGTH) {
      errors.password = `Password must be at least ${PASSWORD_MIN_LENGTH} characters`
    } else if (!PASSWORD_COMPLEXITY_REGEX.test(password)) {
      errors.password =
        'Password must contain one uppercase letter (A-Z), one lowercase letter (a-z), & one number (0-9)'
    }
  }

  // Only Create shows the badges section -- don't let a new admin get
  // created with no permissions at all by accident.
  if (props?.showBadges && (!badges || badges.length === 0)) {
    errors.badges = 'Select a preset or at least one badge before saving.'
  }

  return errors
}

export default reduxForm<FormProps, Props>({
  form: 'adminForm',
  validate,
  enableReinitialize: true,
  destroyOnUnmount: true,
})(AdminForm)
